import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';

import ConfirmBottomSheet from 'components/ConfirmBottomSheet';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import ResponseScreen, { BackgroundTypes, IconTypes } from 'screens/components/ResponseScreen';
import { cancelTravel } from 'services/customerSupportService';
import { extractErrorMessage } from 'utils/generic';

import { Message, ActionButton } from '../RequestStatusInfo/styles';
import TripCardHelp from '../TripCardHelp';

type Props = {
  messageMode?: 'cancel' | 'change';
  travelId: string;
  onBack: () => void;
};

const CancelPendingTrip = ({ messageMode = 'cancel', travelId, onBack }: Props) => {
  const { t } = useStoreTranslation();
  const [showCancelSheet, setShowCancelSheet] = useState(false);
  const [loadingCancel, setLoadingCancel] = useState(false);
  const [cancelSuccess, setCancelSuccess] = useState(false);
  const toast = useToast();
  const history = useHistory();

  const handleCancellationConfirm = async () => {
    setLoadingCancel(true);
    try {
      await cancelTravel({ travelId });
      setCancelSuccess(true);
    } catch (error) {
      toast.add({ type: 'danger', message: extractErrorMessage(error) || t('defaultError.default') });
    } finally {
      setLoadingCancel(false);
    }
  };

  return (
    <>
      {messageMode === 'cancel' ? (
        <TripCardHelp onBack={onBack} title={t('customerSupport.cancelRequest.title')}>
          <Message>{t('customerSupport.cancelRequest.line1')}</Message>
          <Message>{t('customerSupport.cancelRequest.line2')}</Message>
        </TripCardHelp>
      ) : (
        <TripCardHelp onBack={onBack} title={t('customerSupport.changeRequest.title')}>
          <Message>{t('customerSupport.changeRequest.line1')}</Message>
          <Message>{t('customerSupport.changeRequest.line2')}</Message>
          <Message>{t('customerSupport.changeRequest.line3')}</Message>
        </TripCardHelp>
      )}

      <ActionButton
        onClick={() => {
          setShowCancelSheet(true);
        }}
        loading={loadingCancel}
        disabled={loadingCancel}
      >
        {t('customerSupport.cancelRequest.button')}
      </ActionButton>

      <ConfirmBottomSheet
        show={showCancelSheet}
        onClose={() => {
          setShowCancelSheet(false);
        }}
        onConfirm={handleCancellationConfirm}
        title={t('customerSupport.cancelRequest.confirmTitle')}
        content={t('customerSupport.cancelRequest.confirmContent')}
      />

      <ResponseScreen
        show={cancelSuccess}
        text={t('customerSupport.cancelRequest.success.title')}
        subText={t('customerSupport.cancelRequest.success.message')}
        buttonText={t('customerSupport.cancelRequest.success.continue')}
        buttonOnClick={() => {
          history.push('/customerSupport');
        }}
        background={BackgroundTypes.Cloud}
        icon={IconTypes.PaperAirplane}
      />
    </>
  );
};

export default CancelPendingTrip;
